import { Currency } from '@uniswap/sdk-core'
import { PortfolioLogo } from 'components/AccountDrawer/MiniPortfolio/PortfolioLogo'
import { ROBINHOOD_TOKEN_METADATA } from 'constants/robinhoodTokens'
import styled from 'lib/styled-components'
import React from 'react'
import { UniverseChainId } from 'uniswap/src/types/chains'

// Robinhood Chain (4663) has no coverage in Uniswap's logo services, so `useCurrencyInfo` (the normal
// currency→logo path used below) returns nothing and the swap panel shows a blank icon. Resolve 4663
// icons from our curated map by address instead — native → ETH logo, non-curated → local blank (the
// same trust model as the holdings tray, which already renders these correctly).
const ROBINHOOD_ETH_LOGO = 'https://assets.coingecko.com/coins/images/279/small/ethereum.png'
function robinhoodLogoURI(currency?: Currency | null): string | undefined {
  if (!currency || currency.chainId !== UniverseChainId.Robinhood) {
    return undefined
  }
  return currency.isNative ? ROBINHOOD_ETH_LOGO : ROBINHOOD_TOKEN_METADATA[currency.wrapped.address.toLowerCase()]?.logoURI
}

export const MissingImageLogo = styled.div<{ $size?: string; $textColor: string; $backgroundColor: string }>`
  --size: ${({ $size }) => $size};
  border-radius: 100px;
  color: ${({ $textColor }) => $textColor};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  font-size: calc(var(--size) / 3);
  font-weight: 535;
  height: ${({ $size }) => $size ?? '24px'};
  line-height: ${({ $size }) => $size ?? '24px'};
  text-align: center;
  width: ${({ $size }) => $size ?? '24px'};
  display: flex;
  align-items: center;
  justify-content: center;
`

export type AssetLogoBaseProps = {
  symbol?: string | null
  primaryImg?: string | null
  size?: number
  style?: React.CSSProperties
  currency?: Currency | null
}
type AssetLogoProps = AssetLogoBaseProps & { isNative?: boolean; address?: string | null; chainId?: number }

const LogoContainer = styled.div`
  position: relative;
  display: flex;
`

/**
 * Renders an image by prioritizing a list of sources, and then eventually a fallback triangle alert
 */
export default function AssetLogo({ currency, chainId = UniverseChainId.Mainnet, size = 24, style }: AssetLogoProps) {
  const isRobinhood = currency?.chainId === UniverseChainId.Robinhood
  return (
    <LogoContainer style={{ height: size, width: size, ...style }}>
      {isRobinhood ? (
        // Bypass the currency→logo lookup (no 4663 coverage) and render the curated image directly.
        // images=[undefined] → local blank for non-curated tokens (never an attacker-controlled URL).
        <PortfolioLogo images={[robinhoodLogoURI(currency)]} size={size} chainId={chainId} />
      ) : (
        <PortfolioLogo currencies={currency ? [currency] : []} size={size} chainId={chainId} />
      )}
    </LogoContainer>
  )
}
